import type { UIMessage } from '@ai-sdk/ui-utils';

type Chunk = { type: string; payload?: Record<string, unknown> };
type Part = NonNullable<UIMessage['parts']>[number];
type TextPart = Extract<Part, { type: 'text' }> & { id?: string };
type ToolPart = Extract<Part, { type: 'tool-invocation' }> & {
  providerMetadata?: Record<string, Record<string, unknown>>;
};
type ToolCallState = 'partial-call' | 'call' | 'result';

const TEXT_CHUNK_TYPES = new Set(['text-start', 'text-delta', 'text-end']);
const TOOL_CALL_CHUNK_TYPES = new Set([
  'tool-call-input-streaming-start',
  'tool-call-input-streaming-end',
  'tool-call-delta',
  'tool-call',
]);
const NO_OP_CHUNK_TYPES = new Set(['step-start', 'step-finish', 'finish', 'error']);

export class MastraStreamAccumulator {
  private buffer: UIMessage[];

  constructor(initial: readonly UIMessage[] = []) {
    this.buffer = [...initial];
  }

  public get messages(): readonly UIMessage[] {
    return this.buffer;
  }

  public apply(chunk: Chunk): readonly UIMessage[] {
    if (chunk.type === 'start') {
      this.appendAssistantMessage(<string>(chunk.payload?.['messageId'] ?? crypto.randomUUID()));
    } else if (NO_OP_CHUNK_TYPES.has(chunk.type)) {
      // discriminator for no-op events; nothing to mutate
    } else if (TEXT_CHUNK_TYPES.has(chunk.type)) {
      this.applyTextChunk(chunk);
    } else if (TOOL_CALL_CHUNK_TYPES.has(chunk.type)) {
      this.applyToolCallChunk(chunk);
    } else if (chunk.type === 'tool-result') {
      this.applyToolResultChunk(chunk);
    }
    return this.buffer;
  }

  public setMessages(msgs: readonly UIMessage[]): void {
    this.buffer = [...msgs];
  }

  public reset(): void {
    this.buffer = [];
  }

  private appendAssistantMessage(id: string): UIMessage {
    const msg: UIMessage = { id, role: 'assistant', content: '', parts: [] };
    this.buffer.push(msg);
    return msg;
  }

  private lastAssistant(): UIMessage {
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      if (this.buffer[i]!.role === 'assistant') return this.buffer[i]!;
    }
    return this.appendAssistantMessage(crypto.randomUUID());
  }

  private applyTextChunk(chunk: Chunk): void {
    const msg = this.lastAssistant();
    const parts = (msg.parts ??= []);
    const id = <string>(chunk.payload?.['id'] ?? '');
    const isMatchingText = (x: Part): x is TextPart => x.type === 'text' && (<TextPart>x).id === id;
    let part = parts.find(isMatchingText);
    if (!part) {
      part = <TextPart>(<unknown>{ type: 'text', text: '', id });
      parts.push(part);
    }
    if (chunk.type === 'text-delta') {
      part.text += <string>(chunk.payload?.['text'] ?? '');
    }
    msg.content = parts
      .filter((x): x is TextPart => x.type === 'text')
      .map(x => x.text)
      .join('');
  }

  private applyToolCallChunk(chunk: Chunk): void {
    const p = chunk.payload ?? {};
    const isFinal = chunk.type === 'tool-call';
    upsertToolPart(this.lastAssistant(), {
      toolCallId: <string>p['toolCallId'],
      toolName: <string>p['toolName'],
      args: isFinal ? p['args'] : (p['args'] ?? {}),
      state: isFinal ? 'call' : 'partial-call',
      providerMetadata: <ToolPart['providerMetadata']>p['providerMetadata'],
    });
  }

  private applyToolResultChunk(chunk: Chunk): void {
    const p = chunk.payload ?? {};
    upsertToolPart(this.lastAssistant(), {
      toolCallId: <string>p['toolCallId'],
      toolName: <string>(p['toolName'] ?? ''),
      args: p['args'],
      state: 'result',
      result: p['result'] ?? p['output'],
      providerMetadata: <ToolPart['providerMetadata']>p['providerMetadata'],
    });
  }
}

interface ToolPartUpsert {
  toolCallId: string;
  toolName: string;
  args: unknown;
  state: ToolCallState;
  result?: unknown;
  providerMetadata?: ToolPart['providerMetadata'];
}

const upsertToolPart = (msg: UIMessage, upsert: ToolPartUpsert): void => {
  const parts = (msg.parts ??= []);
  const existing = parts.find(
    (p): p is ToolPart =>
      p.type === 'tool-invocation' && p.toolInvocation?.toolCallId === upsert.toolCallId,
  );
  const invocation = {
    toolCallId: upsert.toolCallId,
    toolName: upsert.toolName,
    args: upsert.args,
    state: upsert.state,
    ...(upsert.result !== undefined ? { result: upsert.result } : {}),
  };
  if (existing) {
    existing.toolInvocation = <typeof existing.toolInvocation>invocation;
    if (upsert.providerMetadata !== undefined) {
      existing.providerMetadata = upsert.providerMetadata;
    }
    return;
  }
  parts.push(<ToolPart>(<unknown>{
    type: 'tool-invocation',
    toolInvocation: invocation,
    ...(upsert.providerMetadata !== undefined ? { providerMetadata: upsert.providerMetadata } : {}),
  }));
};
